/**
 * QA Panel — long-press progress indicator.
 *
 * A 5-second hold with no feedback feels broken, so qaPanel renders its OWN
 * transient progress ring positioned over the StatusBar settings button (the
 * same DOM-injection approach floatingButton uses). It does NOT touch the
 * SettingsButton component.
 *
 * The ring is a fixed-position element centered on the button's bounding rect.
 * The fill is a conic-gradient driven by a CSS custom property (`--p`, 0→1)
 * that transitions over the hold duration, so the sweep is GPU-cheap and needs
 * no per-frame JS.
 */

const RING_ID = 'react-lobby-qa-longpress-ring';
const STYLE_ID = 'react-lobby-qa-longpress-style';
const RING_SIZE = 48; // px
const RING_THICKNESS = 4; // px

const STYLE = `
@property --react-lobby-qa-lp {
    syntax: '<percentage>';
    inherits: false;
    initial-value: 0%;
}
#${RING_ID} {
    position: fixed;
    z-index: 2147483646;
    width: ${RING_SIZE}px;
    height: ${RING_SIZE}px;
    border-radius: 50%;
    pointer-events: none;
    --react-lobby-qa-lp: 0%;
    background:
        conic-gradient(#58C4DC var(--react-lobby-qa-lp), rgba(255, 255, 255, 0.18) 0);
    -webkit-mask: radial-gradient(
        farthest-side,
        transparent calc(100% - ${RING_THICKNESS}px),
        #000 calc(100% - ${RING_THICKNESS}px)
    );
    mask: radial-gradient(
        farthest-side,
        transparent calc(100% - ${RING_THICKNESS}px),
        #000 calc(100% - ${RING_THICKNESS}px)
    );
    opacity: 0;
    transition: opacity 120ms ease;
}
#${RING_ID}.active { opacity: 1; }
`;

export interface LongPressIndicator {
    /** Show the ring centered over `target` and begin filling over `durationMs`. */
    show(target: Element, durationMs: number): void;
    /** Hide and reset the ring. */
    hide(): void;
    /** Remove the ring and its style from the DOM. */
    destroy(): void;
}

export function createLongPressIndicator(): LongPressIndicator {
    if (typeof document === 'undefined') {
        return {
            show: () => undefined,
            hide: () => undefined,
            destroy: () => undefined,
        };
    }

    let ring: HTMLDivElement | null = null;

    const ensureStyle = (): void => {
        if (document.getElementById(STYLE_ID)) return;
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = STYLE;
        document.head.appendChild(style);
    };

    const ensureRing = (): HTMLDivElement => {
        if (ring) return ring;
        ensureStyle();
        const el = document.createElement('div');
        el.id = RING_ID;
        ring = el;
        return el;
    };

    return {
        show(target: Element, durationMs: number) {
            const el = ensureRing();
            const rect = target.getBoundingClientRect();
            const cx = rect.left + rect.width / 2;
            const cy = rect.top + rect.height / 2;
            el.style.left = `${Math.round(cx - RING_SIZE / 2)}px`;
            el.style.top = `${Math.round(cy - RING_SIZE / 2)}px`;

            if (!el.isConnected) {
                document.body.appendChild(el);
            }

            // Reset the fill instantly, then animate it to full over the hold.
            el.style.transition = 'opacity 120ms ease';
            el.style.setProperty('--react-lobby-qa-lp', '0%');
            el.classList.add('active');

            // Next frame: enable the fill transition and drive it to 100%.
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!ring) return;
                    ring.style.transition = `opacity 120ms ease, --react-lobby-qa-lp ${durationMs}ms linear`;
                    ring.style.setProperty('--react-lobby-qa-lp', '100%');
                });
            });
        },
        hide() {
            if (!ring) return;
            ring.classList.remove('active');
            ring.style.transition = 'opacity 120ms ease';
            ring.style.setProperty('--react-lobby-qa-lp', '0%');
            if (ring.isConnected) {
                ring.parentNode?.removeChild(ring);
            }
        },
        destroy() {
            if (ring?.isConnected) {
                ring.parentNode?.removeChild(ring);
            }
            ring = null;
            const style = document.getElementById(STYLE_ID);
            style?.parentNode?.removeChild(style);
        },
    };
}
